import React, { useEffect, useRef, useState } from "react";
import { IconButton, Tooltip, Box, Typography } from "@mui/material";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import { IMessage } from "../../../models/user/InsureAssist/InsureAssist";

interface VoiceChatToggleProps {
  getConvoId: () => string | null;
  getMessages: () => IMessage[];
  setMessages: (updater: (prev: IMessage[]) => IMessage[]) => void;
  customerId?: string | number | null;
  iconColor?: string;
  onStatusChange?: (status: { active: boolean; speaking: boolean }) => void;
  selectedDeviceId?: string;
  voiceStartTs?: number | null;
  onReset?: () => void;
  resetConversationRef?: React.MutableRefObject<(() => void) | null>;
  // Allows parent components to stop voice (switch to text) without clearing messages
  stopVoiceRef?: React.MutableRefObject<(() => void) | null>;
  onCard?: (card: any) => void;
}

const VoiceChatToggle: React.FC<VoiceChatToggleProps> = ({
  getConvoId,
  getMessages,
  setMessages,
  customerId,
  iconColor,
  onStatusChange,
  selectedDeviceId,
  voiceStartTs,
  onReset,
  resetConversationRef,
  stopVoiceRef,
  onCard,
}) => {
  const [isVoiceActive, setIsVoiceActive] = useState<boolean>(false);
  const [isUserSpeaking, setIsUserSpeaking] = useState<boolean>(false);
  const socketRef = useRef<WebSocket | null>(null);

  // Audio and streaming state (adapted from InsureAssist)
  const realtimeAudioContextRef = useRef<AudioContext | null>(null);
  const realtimeAudioProcessorRef = useRef<ScriptProcessorNode | null>(null);
  const realtimeAudioStreamRef = useRef<MediaStream | null>(null);
  const currentAudioSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const realtimeAudioQueueRef = useRef<Uint8Array[]>([]);
  const realtimeIsPlayingRef = useRef<boolean>(false);
  const responseMarkdownBufferRef = useRef<Map<string, string>>(new Map());
  const lastResponseIdRef = useRef<string | null>(null);
  const streamingMessageIndexRef = useRef<Map<string, number>>(new Map());
  const [elapsedTime, setElapsedTime] = useState<number>(0); // total elapsed time in ms
const timerRef = useRef<NodeJS.Timeout | null>(null);
const startTimeRef = useRef<number | null>(null);

useEffect(() => {
  if (isVoiceActive) {
    // Start or resume the timer
    startTimeRef.current = Date.now();
    timerRef.current = setInterval(() => {
      if (startTimeRef.current !== null) {
        const diff = Date.now() - startTimeRef.current;
        setElapsedTime(prev => prev + diff);
        startTimeRef.current = Date.now(); // reset reference for next tick
      }
    }, 1000);
  } else {
    // Pause timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (startTimeRef.current) {
      const diff = Date.now() - startTimeRef.current;
      setElapsedTime(prev => prev + diff);
      startTimeRef.current = null;
    }
  }

  return () => {
    if (timerRef.current) clearInterval(timerRef.current);
  };
}, [isVoiceActive]);

  useEffect(() => {
    // lazy-load marked if missing
    if (!(window as any).marked) {
      const script = document.createElement("script");
      script.src = "https://cdn.jsdelivr.net/npm/marked@13.0.2/marked.min.js";
      script.async = true;
      document.body.appendChild(script);
      return () => {
        try {
          document.body.removeChild(script);
        } catch (e) {}
      };
    }
  }, []);

  useEffect(() => {
    try {
      if (typeof (onStatusChange as any) === "function") {
        (onStatusChange as any)({ active: isVoiceActive, speaking: isUserSpeaking });
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVoiceActive, isUserSpeaking]);

  const stopCurrentAudioPlayback = () => {
    try {
      if (currentAudioSourceRef.current) {
        currentAudioSourceRef.current.stop();
        currentAudioSourceRef.current.disconnect();
      }
    } catch (e) {}
    currentAudioSourceRef.current = null;
  };

  const base64EncodeAudio = (uint8Array: Uint8Array) => {
    let binary = "";
    const chunkSize = 0x8000;
    for (let i = 0; i < uint8Array.length; i += chunkSize) {
      const chunk = uint8Array.subarray(i, i + chunkSize);
      binary += String.fromCharCode.apply(null, Array.from(chunk) as any);
    }
    return btoa(binary);
  };

  const floatTo16BitPCM = (float32Array: Float32Array) => {
    const buffer = new ArrayBuffer(float32Array.length * 2);
    const view = new DataView(buffer);
    let offset = 0;
    for (let i = 0; i < float32Array.length; i++, offset += 2) {
      const s = Math.max(-1, Math.min(1, float32Array[i]));
      view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    }
    return buffer;
  };

  const concatenateBuffers = (buffer1: ArrayBuffer, buffer2: ArrayBuffer) => {
    const tmp = new Uint8Array(buffer1.byteLength + buffer2.byteLength);
    tmp.set(new Uint8Array(buffer1), 0);
    tmp.set(new Uint8Array(buffer2), buffer1.byteLength);
    return tmp.buffer;
  };

  const createWavBuffer = (pcmBuffer: ArrayBuffer, sampleRate: number) => {
    const wavHeader = new ArrayBuffer(44);
    const view = new DataView(wavHeader);
    const writeString = (v: DataView, offset: number, str: string) => {
      for (let i = 0; i < str.length; i++)
        v.setUint8(offset + i, str.charCodeAt(i));
    };
    writeString(view, 0, "RIFF");
    view.setUint32(4, 36 + pcmBuffer.byteLength, true);
    writeString(view, 8, "WAVE");
    writeString(view, 12, "fmt ");
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    writeString(view, 36, "data");
    view.setUint32(40, pcmBuffer.byteLength, true);
    return concatenateBuffers(wavHeader, pcmBuffer);
  };

  const playPCM = (pcmBuffer: Uint8Array, callback: () => void) => {
    const ctx = realtimeAudioContextRef.current;
    if (!ctx) return callback();
    const wavBuffer: ArrayBuffer = createWavBuffer(
      new Uint8Array(pcmBuffer).buffer as ArrayBuffer,
      24000
    ) as ArrayBuffer;
    const arrBuf: ArrayBuffer = (wavBuffer as ArrayBuffer).slice(0);
    ctx.decodeAudioData(
      arrBuf,
      (audioBuffer) => {
        const source = ctx.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(ctx.destination);
        source.onended = callback;
        try {
          source.start(0);
          currentAudioSourceRef.current = source;
        } catch (e) {
          callback();
        }
      },
      () => callback()
    );
  };

  const playNextAudio = () => {
    const queue = realtimeAudioQueueRef.current;
    if (queue.length > 0) {
      realtimeIsPlayingRef.current = true;
      const audioData = queue.shift()!;
      playPCM(audioData, playNextAudio);
    } else {
      realtimeIsPlayingRef.current = false;
    }
  };

  const stopRealtimeRecording = () => {
    try {
      if (realtimeAudioProcessorRef.current) {
        realtimeAudioProcessorRef.current.disconnect();
      }
    } catch (e) {}
    try {
      if (realtimeAudioStreamRef.current) {
        realtimeAudioStreamRef.current.getTracks().forEach((t) => t.stop());
      }
    } catch (e) {}
    stopCurrentAudioPlayback();
    realtimeAudioProcessorRef.current = null;
    realtimeAudioStreamRef.current = null;
  };

  const startRealtimeRecording = (wsParam?: WebSocket) => {
    // Avoid creating duplicate processors/contexts if already recording
    if (realtimeAudioProcessorRef.current || realtimeAudioContextRef.current) {
      try {
        // Try to resume if context is suspended
        const ctx = realtimeAudioContextRef.current as any;
        if (ctx && ctx.state === "suspended" && typeof ctx.resume === "function") {
          ctx.resume().catch(() => {});
        }
      } catch {}
      return;
    }
    const constraints: MediaStreamConstraints = {
      audio: selectedDeviceId && selectedDeviceId !== "default"
        ? { deviceId: { exact: selectedDeviceId } as any }
        : true,
    };
    navigator.mediaDevices
      .getUserMedia(constraints)
      .then((stream) => {
        const AudioCtx =
          (window as any).AudioContext || (window as any).webkitAudioContext;
        realtimeAudioContextRef.current = new AudioCtx({ sampleRate: 24000 });
        realtimeAudioStreamRef.current = stream;
        const ctx = realtimeAudioContextRef.current!;
        const source = ctx.createMediaStreamSource(stream);
        const processor = ctx.createScriptProcessor(8192, 1, 1);
        realtimeAudioProcessorRef.current = processor;
        processor.onaudioprocess = (event) => {
          const wsToUse = wsParam || socketRef.current;
          if (wsToUse && wsToUse.readyState === WebSocket.OPEN) {
            const inputBuffer = event.inputBuffer.getChannelData(0);
            const pcm = new Uint8Array(floatTo16BitPCM(inputBuffer));
            const base64PCM = base64EncodeAudio(pcm);
            const chunkSize = 4096;
            for (let i = 0; i < base64PCM.length; i += chunkSize) {
              const chunk = base64PCM.slice(i, i + chunkSize);
              try {
                wsToUse.send(
                  JSON.stringify({
                    type: "input_audio_buffer.append",
                    audio: chunk,
                  })
                );
              } catch (e) {}
            }
          }
        };
        source.connect(processor);
        processor.connect(ctx.destination);
      })
      .catch(() => {
        setIsVoiceActive(false);
      });
  };

  const toggleVoice = () => {
   const realtimeWsUrlBase = process.env.REACT_APP_CHAT_REALTIME_VOICE_URL;
   const realtimeWsUrl =
     realtimeWsUrlBase &&
     (customerId === undefined || customerId === null || customerId === ""
       ? realtimeWsUrlBase
       : `${realtimeWsUrlBase}${realtimeWsUrlBase.includes("?") ? "&" : "?"}customerId=${encodeURIComponent(
           String(customerId)
         )}`);
    if (!isVoiceActive && realtimeWsUrl ) {
      try {
        // Start mic immediately on user gesture to satisfy permissions
        // WebSocket may connect slightly later; the processor will use it once available
        startRealtimeRecording();
        const ws = new WebSocket(realtimeWsUrl);
        socketRef.current = ws;
        ws.onopen = () => {
          setIsVoiceActive(true);
        };
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            switch (data.type) {
              case "response.card": {
                try {
                  const payloadStr = data.payload;
                  const parsed = typeof payloadStr === "string" ? JSON.parse(payloadStr) : payloadStr;
                  if (parsed && onCard) {
                    onCard(parsed);
                  }
                } catch {}
                break;
              }
              case "session.created": {
                // Ensure recording is started and bound; guarded against duplicates
                startRealtimeRecording(ws);
                break;
              }
              case "input_audio_buffer.speech_started": {
                stopCurrentAudioPlayback();
                realtimeAudioQueueRef.current = [];
                realtimeIsPlayingRef.current = false;
                setIsUserSpeaking(true);
                break;
              }
              case "input_audio_buffer.speech_stopped": {
                try {
                  ws.send(
                    JSON.stringify({ type: "input_audio_buffer.commit" })
                  );
                  ws.send(
                    JSON.stringify({
                      type: "response.create",
                      response: { modalities: ["text", "audio"] },
                    })
                  );
                } catch (e) {}
                setIsUserSpeaking(false);
                break;
              }
              case "conversation.item.input_audio_transcription.completed": {
                const userText: string = data.transcript;
                const convoId = getConvoId() || "";
                const prev = getMessages();
                const level =
                  prev.length !== 0 ? prev[prev.length - 1].level + 1 : 0;
                const messageObj: IMessage = {
                  content: userText,
                  creationTime: new Date().toString(),
                  level,
                  sender: "customer",
                  type: "text",
                  convoId,
                };
                (messageObj as any).channel = "voice";
                setMessages((prevMsgs) => [...prevMsgs, messageObj]);
                break;
              }


              case "response.output_audio_transcript.delta": {
                const transcript: string = data.delta || "";
                const responseId: string = data.response_id || "default";
                lastResponseIdRef.current = responseId;
                const buf = responseMarkdownBufferRef.current;
                const nextContent = (buf.get(responseId) || "") + transcript;
                buf.set(responseId, nextContent);

                const marked = (window as any).marked;
                const html = marked ? marked.parse(nextContent) : undefined;

                setMessages((prev) => {
                  const map = streamingMessageIndexRef.current;
                  let idx = map.get(responseId);
                  if (idx === undefined) {
                    const newMsg: IMessage = {
                      content: nextContent,
                      creationTime: new Date().toString(),
                      level:
                        prev.length !== 0 ? prev[prev.length - 1].level + 1 : 0,
                      sender: "system",
                      type: "text",
                      convoId: getConvoId() || "",
                    } as IMessage;
                    (newMsg as any).contentHtml = html;
                    (newMsg as any).responseId = responseId;
                    (newMsg as any).streaming = true;
                    (newMsg as any).channel = "voice";
                    const arr = [...prev, newMsg];
                    map.set(responseId, arr.length - 1);
                    return arr;
                  } else {
                    const arr = [...prev];
                    const current: any = { ...(arr[idx] as any) };
                    current.content = nextContent;
                    current.contentHtml = html;
                    arr[idx] = current as IMessage;
                    return arr;
                  }
                });
                break;
              }
              case "response.output_audio_transcript.done": {
                const responseId: string =
                  data.response_id || lastResponseIdRef.current || "default";
                streamingMessageIndexRef.current.delete(responseId);
                break;
              }
              case "response.output_audio.delta": {
                if (data.delta) {
                  const audioData = Uint8Array.from(atob(data.delta), (c) =>
                    c.charCodeAt(0)
                  );
                  realtimeAudioQueueRef.current.push(audioData);
                  if (!realtimeIsPlayingRef.current) {
                    playNextAudio();
                  }
                }
                break;
              }
              case "response.output_audio.done": {
                realtimeIsPlayingRef.current = false;
                break;
              }
              default:
                break;
            }
          } catch (e) {
            // ignore unrecognized payloads
          }
        };
        ws.onclose = () => {
          cleanup();
        };
        ws.onerror = () => {
          cleanup();
        };
      } catch (e) {
        // failed to open
      }
    } else {
      cleanup();
    }
  };

  const cleanup = () => {
    setIsVoiceActive(false);
    setIsUserSpeaking(false);
    if (socketRef.current) {
      try {
        socketRef.current.close();
      } catch (e) {}
    }
    socketRef.current = null;
    stopRealtimeRecording();
    try {
      if (
        realtimeAudioContextRef.current &&
        realtimeAudioContextRef.current.state !== "closed"
      ) {
        realtimeAudioContextRef.current.close();
      }
    } catch (e) {}
    realtimeAudioContextRef.current = null;
    realtimeAudioQueueRef.current = [];
    realtimeIsPlayingRef.current = false;
  };

  const resetConversation = () => {
    cleanup();
    setMessages(() => []);
    setElapsedTime(0);
    // Reset timer references
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    startTimeRef.current = null;
    if (onReset) {
      onReset();
    }
  };

  // Expose resetConversation function to parent
  useEffect(() => {
    if (resetConversationRef) {
      resetConversationRef.current = resetConversation;
    }
  }, [resetConversationRef]);

  // Expose stop voice (cleanup only) for switching to text mode without clearing messages
  useEffect(() => {
    if (stopVoiceRef) {
      stopVoiceRef.current = () => {
        cleanup();
      };
    }
  }, [stopVoiceRef]);

  useEffect(() => {
    return () => {
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
      <Tooltip
        title={
          isVoiceActive ? "Stop Voice Conversation" : "Start Voice Conversation"
        }
      >
        <IconButton
          size="small"
          sx={{ color: "#6b7280", "&:hover": { bgcolor: "#f3f4f6" }, p: { xs: 0.5, md: 1 } }}
          onClick={toggleVoice}
        >
          <GraphicEqIcon
            sx={{
              fontSize: { xs: 20, md: 25 },
              color: isVoiceActive ? "#ef4444" : undefined,
              animation: isUserSpeaking
                ? "equalize 1s infinite ease-in-out"
                : undefined,
              transformOrigin: "50% 50%",
              "@keyframes equalize": {
                "0%": { transform: "scaleY(0.7)" },
                "25%": { transform: "scaleY(1.1)" },
                "50%": { transform: "scaleY(0.85)" },
                "75%": { transform: "scaleY(1.2)" },
                "100%": { transform: "scaleY(0.7)" },
              },
            }}
          />
        </IconButton>
      </Tooltip>
      {elapsedTime > 0 && (
  <Typography sx={{ fontSize: 12, color: "#111827", fontWeight: 600 }}>
    {new Date(elapsedTime).toISOString().substring(14, 19)}
  </Typography>
)}
    </Box>
  );
};

export default VoiceChatToggle;
 